/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2018, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 *
 * @typedef {Object} FormatSpec
 * @property {Boolean} useGrouping
 * @property {type} type  - 'number' 'currency', 'percent', 'date', 'datetime', 'time'
 * @property {Boolean} convertPercent convert decimal to percentage (multiply number by 100)
 *
 * @property {Number} maximumFractionDigits  - maximum fraction digit.
 * @property {Number} minimumFractionDigits  - maximum fraction digit.
 * @property {String}  decimalFormatLength  { 'short' | 'long' }
 *
 * @property {String} currencyCode e.g. 'AUD', 'USD', etc.
 * @property {String} currencyDisplay   { 'symbol' | 'code' }
 *
 * @property {String} dateStyle  'short' 'medium' 'long' 'full'
 * @property {String} displayOrder  'YMD' MDY' 'DMY'
 *
 * @property {String} [timezone] only for getFormat()
 * @property {String} [locale] only for getFormat()
 * @property {Boolean} [local] only for getFormat(),  true if it is set from widget, or it is from MetaData
 */

/**
 * @typedef {Object} PromptValueSpec
 * @property {String} name
 * @property {Object} capabilities
 * @property {String} dataType
 * @property {String} values
 * @property {String} [modelFilterItem]
 *
 */

/**
 * @typedef {Object} SortSpec
 * @property {String} type - 'asc' 'desc' 'null'
 * @property {number} priority - 'asc' 'desc' 'null'
 * @property {String} by { 'caption' | 'value' }
 * @property {String} context - metadata column id used for the sort context
 * @property {Object} custom - custom sort
 * @property {String[]} custom.top - members of column to place at the top
 * @property {Boolean} custom.enabled - indicate if custom sort is applied
 *
 */

/**
 * @typedef {Object} BinningSpec
 * @property {number} bins number of bins
 * @property {label} label the label for binning group
 *
 */

/**
 * @typedef {Object} TopBottomInfo
 * @property {String} type - 'topcount' 'toppercent' 'bottomcount' 'bottompercent'
 * @property {number} value - 'asc' 'desc' 'null'
 * @property {boolean} rank - 'caption' 'value'
 * @property {String} context - metadata column id used for the rank context
 * @property {string} aggregate
 *
 */

/**
 *
 * @typedef {Object} SlotDefinition
 * @property {String} dataset
 * @property {String} group
 * @property {String} role
 * @property {Boolean} hidden
 * @property {String} id
 * @property {String} caption
 * @property {Boolean} optional
 * @property {String} sort
 * @property {Number} sortOrder
 * @property {Boolean} sortable
 * @property {Boolean} stackItems
 * @property {String} type
 * @property {Boolean} multiMeasure
 *
 */

/**
 * @typedef MappingInfo
 * @property {SlotAPI} slot - slot where the the dataitem blongs
 * @property {number} indexInSlot - data item index in the slot
 * @property {DataItemAPI} dataItem - dataItem object
 *
 */

/**
 * @typedef {Object} Dataset
 * @property {String} name
 */

/**
 * @typedef {Object} PreferredSize
 * @property {Number} height
 * @property {Number} width
 */

/**
 * @typedef {Object} ThemeMappingEntry
 * @property {String} id
 * @property {Array} mapping
 */


/**
 * @typedef {Object} ThumbnailConfig
 * @property {String[]} unsupportedBrowsers
 */

/**
 * @typedef {Object} editorOptions
 * @property {String} placement - placement overrides (eg auto)
 * @property {String} attachPoint - the class of the dom node to be attached to
 * @property {Boolean} forceOpen - when false, a filter entry can be replaced without launching the UI. - placement overrides (eg auto)
 */

/**
 * @description Property Prefixes Object is dependent on the visualization but it contains visualization slots/definition with their
 * mapped values
 *
 * @typedef {Object} propertyPrefixes
 * @property {String} categories
 * @property {String} color
 * @property {String} column
 * @property {String|Array} dataPoint
 * @property {String} legend
 * @property {String} row
 * @property {String} values
 * @property {String} x
 * @property {String} y
 * @property {String} grid_cols
 * @property {String} size
 * @property {String} width
 * @property {String} series
 */

/**
 * @typedef {Object} ItemContext
 * @property {String} origin The origin of the {@link PageContextEntry} item.
 * @property {String} sourceId The data source id of the item.
 * @property {String} itemId The column id.
 * @property {String[]} itemIds The array of column ids.
 * @property {String} [scope] The scope (page id) of the item.
 * @property {String} [eventGroupId] The event (connection) group id of the item.
 * @property {String} [eventSourceId] The content id of the item.
 * @property {boolean} [_strictMatch] Match each supplied property values.
 * @property {String} [aggregationType] The aggregation type.
 */

/**
 * @typedef {Object} FilterContext
 * @property {Object[]} values Array of filter values object.
 * @property {Number} [from] Start range value.
 * @property {Number} [to] End range value.
 * @property {Boolean} [exclude] Exclusions filter values.
 * @property {Object} [conditionsInfo] filter conditions object {@link ConditionsInfo}.
 */

/**
 * @typedef {Object} QueryWarning
 * @property {String} resourceId unique resource indentifier of the warning message
 * @property {Object} params resource string parameters
 */